import React, { useEffect, useId, useMemo } from 'react';
import styled from 'styled-components';
import { EditorContent, ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';

import { useRichTextEditor, MentionSuggestion } from '../../hooks/useRichTextEditor';
import { EditorBubbleMenu } from '../rich-editor/editor-bubble-menu';
import { MentionList, MentionListRef } from './mention-list';
import { context } from '../../app/context';

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onChangeText?: (value: string) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  className?: string;
  autoFocus?: boolean;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  value,
  onChange,
  onChangeText,
  handleKeyDown,
  className = '',
  autoFocus = false,
}) => {
  const key = useId();
  const space = context.space;

  // Get all space members for mention suggestions
  const mentionItems: MentionSuggestion[] = useMemo(() => {
    return space.members.map((member) => ({
      id: member._id,
      label: member.fullName,
      avatar: member.avatar,
      initials: member.initials,
    }));
  }, [space.members]);

  // Configure mention suggestion
  const mentionSuggestion = useMemo(
    () => ({
      items: ({ query }: { query: string }) => {
        return mentionItems
          .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 5);
      },
      render: () => {
        let component: ReactRenderer<MentionListRef>;
        let popup: TippyInstance[];

        return {
          onStart: (props: any) => {
            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },

          onUpdate(props: any) {
            component.updateProps(props);

            if (!props.clientRect) {
              return;
            }

            popup[0].setProps({
              getReferenceClientRect: props.clientRect,
            });
          },

          onKeyDown(props: any) {
            if (props.event.key === 'Escape') {
              popup[0].hide();

              return true;
            }

            return component.ref?.onKeyDown(props) || false;
          },

          onExit() {
            popup[0].destroy();
            component.destroy();
          },
        };
      },
    }),
    [mentionItems],
  );

  const { editor, isTextSelected } = useRichTextEditor({
    key,
    value: value || '',
    autoFocus,
    onUpdate: (text) => {
      onChange(text);
    },
    onUpdateText: (text) => {
      onChangeText && onChangeText(text);
    },
    placeholder: 'Leave a comment...',
    mentionSuggestion,
  });

  useEffect(() => {
    if (editor && value === '') {
      // v3 emits an update by default; keep the v2 silent behaviour.
      editor.commands.clearContent({ emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div style={{ position: 'relative' }}>
      {editor && isTextSelected && <EditorBubbleMenu editor={editor} />}
      <Input
        editor={editor}
        onKeyDown={handleKeyDown}
        className={className}
        autoFocus={autoFocus}
      />
    </div>
  );
};

const Input = styled(EditorContent)`
  outline: none;

  p {
    font-size: 1.4rem;
  }
`;
